import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaChevronDown, FaChevronUp, FaInfoCircle } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// The four bands the API scores against. Kept in the same order and with the
// same boundaries as utils/examReadiness.js — if one moves, both move.
const LEVELS = [
    { min: 80, label: "Exam Ready", text: "text-green-600", bg: "bg-green-500", chip: "bg-green-50 text-green-700" },
    { min: 60, label: "Almost Ready", text: "text-blue-600", bg: "bg-blue-500", chip: "bg-blue-50 text-blue-700" },
    { min: 40, label: "Needs Work", text: "text-amber-600", bg: "bg-amber-500", chip: "bg-amber-50 text-amber-700" },
    { min: 0, label: "Not Ready", text: "text-red-600", bg: "bg-red-500", chip: "bg-red-50 text-red-700" },
];
const bandFor = (score) =>
    score == null ? LEVELS[LEVELS.length - 1] : LEVELS.find((l) => score >= l.min) || LEVELS[LEVELS.length - 1];

const COMPONENT_LABELS = {
    performance: "Recent performance",
    coverage: "Topic coverage",
    consistency: "Study consistency",
    difficulty: "Difficulty readiness",
    stability: "Performance stability",
};

const STATUS_CHIPS = {
    struggling: { label: "Struggling", className: "bg-red-50 text-red-600" },
    thin: { label: "Barely started", className: "bg-amber-50 text-amber-700" },
    not_started: { label: "Not started", className: "bg-gray-100 text-gray-600" },
};

// One weighted part of a score. The weight is shown because the whole point of
// the model is that a student can see WHY the number is what it is — and an
// unmeasurable part says so rather than quietly sitting at its fallback.
const ComponentBar = ({ name, part }) => {
    if (!part) return null;
    const band = bandFor(part.score);
    return (
        <div>
            <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">
                    {COMPONENT_LABELS[name] || name}
                    <span className="text-gray-400"> · {Math.round((part.weight || 0) * 100)}%</span>
                </span>
                <span className={`font-semibold ${part.measurable === false ? "text-gray-400" : "text-gray-700"}`}>
                    {part.measurable === false ? "No data" : `${part.score}%`}
                </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                    className={`h-1.5 rounded-full ${part.measurable === false ? "bg-gray-300" : band.bg}`}
                    style={{ width: `${Math.max(0, Math.min(100, part.score || 0))}%` }}
                />
            </div>
            {part.measurable === false && part.reason && (
                <p className="text-[11px] text-gray-400 mt-1">{part.reason}</p>
            )}
        </div>
    );
};

// General readiness for one exam.
const ExamCard = ({ exam, general, fallbackScore }) => {
    const score = general ? general.score : fallbackScore;
    const band = bandFor(score);
    const penalty = general && general.balancePenalty;

    return (
        <div className="bg-white rounded-xl shadow-sm p-6">
            <p className="text-sm font-semibold text-gray-500">{exam} Readiness</p>
            <p className={`text-4xl font-bold mt-2 ${band.text}`}>{score == null ? "—" : `${score}%`}</p>
            <span className={`inline-block mt-2 text-xs font-semibold px-2 py-1 rounded-full ${band.chip}`}>
                {score == null ? "No data yet" : band.label}
            </span>

            {general && general.components && (
                <div className="mt-4 space-y-3">
                    {Object.entries(general.components).map(([name, part]) => (
                        <ComponentBar key={name} name={name} part={part} />
                    ))}
                </div>
            )}

            {penalty && penalty.applied > 0 && (
                <p className="mt-4 text-xs text-amber-700 bg-amber-50 rounded-lg p-2 flex gap-2">
                    <FaInfoCircle className="mt-0.5 shrink-0" />
                    <span>
                        −{penalty.applied} for balance: {penalty.weakestSubject} ({penalty.weakestScore}%) is{" "}
                        {penalty.gap} points below the {penalty.averageScore}% average.
                    </span>
                </p>
            )}

            {general && general.subjects && general.subjects.length > 0 && (
                <p className="mt-3 text-[11px] text-gray-400">
                    Across {general.subjects.length} subject{general.subjects.length === 1 ? "" : "s"}
                    {general.totalAnswers ? ` · ${general.totalAnswers} questions answered` : ""}
                </p>
            )}
        </div>
    );
};

const SubjectRow = ({ subject }) => {
    const [open, setOpen] = useState(false);
    const band = bandFor(subject.score);
    const coverage = subject.components && subject.components.coverage;

    return (
        <div className="bg-white rounded-xl shadow-sm">
            <button onClick={() => setOpen((v) => !v)} className="w-full text-left p-4">
                <div className="flex justify-between items-start gap-3 mb-2">
                    <div className="min-w-0">
                        <span className="font-medium text-gray-800">{subject.subject}</span>
                        <span className={`ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full ${band.chip}`}>
                            {band.label}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="font-bold text-gray-700">{subject.score}%</span>
                        {open ? <FaChevronUp size={12} className="text-gray-400" /> : <FaChevronDown size={12} className="text-gray-400" />}
                    </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className={`h-2 rounded-full ${band.bg}`} style={{ width: `${subject.score}%` }} />
                </div>
                <p className="text-[11px] text-gray-400 mt-2">
                    {subject.attempted
                        ? `${subject.attempted} questions answered${subject.accuracy != null ? ` · ${subject.accuracy}% correct` : ""}`
                        : "No practice yet"}
                    {coverage && coverage.measurable && coverage.topicsTotal
                        ? ` · ${coverage.topicsCovered} of ${coverage.topicsTotal} topics covered`
                        : ""}
                </p>
            </button>

            {open && subject.components && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                    {["performance", "coverage", "consistency", "difficulty"].map((name) => (
                        <ComponentBar key={name} name={name} part={subject.components[name]} />
                    ))}
                    {subject.cappedForThinData && (
                        <p className="text-[11px] text-gray-500 bg-gray-50 rounded-lg p-2">
                            Held below Exam Ready until more questions are answered in this subject.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

const Readiness = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const studentId = params.get("studentId");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchReadiness = async () => {
            try {
                const url = new URL(`${API_BASE_URL}/api/readiness`);
                if (studentId) url.searchParams.set("studentId", studentId);
                const res = await fetch(url, { headers: authHeaders() });
                const json = await res.json();
                if (!res.ok) throw new Error(json.message || "Failed to load readiness");
                setData(json.data);
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchReadiness();
    }, [studentId]);

    const general = (data && data.general) || {};
    // Weakest first — this page is a to-do list, not a scoreboard.
    const subjects = [...((data && data.subjects) || [])].sort((a, b) => a.score - b.score);
    const recommendations = (data && data.recommendations) || [];

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="text-white"><FaArrowLeft size={18} /></button>
                <h1 className="text-xl font-bold text-white">Exam Readiness</h1>
            </nav>

            <div className="max-w-3xl mx-auto p-6 space-y-6">
                {loading && <p className="text-gray-500">Loading...</p>}

                {!loading && data && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <ExamCard exam="WAEC" general={general.WAEC} fallbackScore={data.waecScore} />
                            <ExamCard exam="JAMB" general={general.JAMB} fallbackScore={data.jambScore} />
                        </div>

                        {recommendations.length > 0 && (
                            <div className="bg-white rounded-xl shadow-sm p-5">
                                <h3 className="font-semibold text-gray-800">Study these next</h3>
                                <p className="text-xs text-gray-500 mt-1 mb-3">
                                    Picked from the weakest subjects — half-learned topics first, then the ones not yet opened.
                                </p>
                                <div className="space-y-2">
                                    {recommendations.map((r) => {
                                        const chip = STATUS_CHIPS[r.status] || STATUS_CHIPS.not_started;
                                        return (
                                            <div key={`${r.subject}-${r.topic}`} className="flex items-start justify-between gap-3 bg-gray-50 rounded-lg p-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 truncate">{r.topic}</p>
                                                    <p className="text-xs text-gray-500">{r.subject} · {r.reason}</p>
                                                </div>
                                                <span className={`text-[11px] font-semibold px-2 py-1 rounded-full shrink-0 ${chip.className}`}>
                                                    {chip.label}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div>
                            <h3 className="font-semibold text-gray-800 mb-1">Per-subject readiness</h3>
                            <p className="text-xs text-gray-500 mb-3">Weakest first. Tap a subject to see what's holding the score.</p>
                            {subjects.length === 0 && <p className="text-sm text-gray-500">No practice history yet.</p>}
                            <div className="space-y-2">
                                {subjects.map((s) => (
                                    <SubjectRow key={s.subject} subject={s} />
                                ))}
                            </div>
                        </div>

                        <button onClick={() => navigate(`/study-plan${studentId ? `?studentId=${studentId}` : ""}`)}
                            className="text-blue600 font-semibold text-sm">
                            See what to study next →
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default Readiness;
